import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wine, ChefHat, Hotel, Crown, Tv, Award, ArrowRight } from "lucide-react";
import { useLang } from "../contexts/LangContext";

const SLIDES = [
  { id: "wines", key: "wines", img: "/api/static/tiles/tile_vini.png", to: "/vini", icon: Wine, accent: "#E2A03F" },
  { id: "boxes", key: "boxes", img: "/api/static/tiles/tile_box.png", to: "/box", icon: ChefHat, accent: "#C9A961" },
  { id: "experiences", key: "experiences", img: "/api/static/tiles/tile_esperienze.png", to: "/esperienze", icon: Hotel, accent: "#E2A03F" },
  { id: "club", key: "club", img: "https://images.pexels.com/photos/33749231/pexels-photo-33749231.jpeg", to: "/club", icon: Crown, accent: "#C9A961" },
  { id: "tv", key: "tv", img: "/api/static/tiles/tile_tv.png", to: "/tv", icon: Tv, accent: "#C9A961" },
  { id: "franchising", key: "franchising", img: "/api/static/tiles/franchising_storefront.png", to: "/franchising", icon: Award, accent: "#E2A03F" },
];

const SLIDE_DURATION_MS = 5500;

export default function HomeHeroCarousel() {
  const { t } = useLang();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const tt = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), SLIDE_DURATION_MS);
    return () => clearInterval(tt);
  }, []);

  const resolve = (src) => (src.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src);

  return (
    <section data-testid="home-hero-carousel" className="relative h-[85vh] min-h-[560px] md:h-[90vh] md:min-h-[640px] overflow-hidden bg-[#0a0a0a]">
      {SLIDES.map((s, i) => {
        const eyebrow = t(`carousel.${s.key}.eyebrow`, "");
        const title = t(`carousel.${s.key}.title`, "");
        const titleAccent = t(`carousel.${s.key}.title_accent`, "");
        const subtitle = t(`carousel.${s.key}.subtitle`, "");
        const ctaLabel = t(`carousel.${s.key}.cta`, "");
        return (
          <div
            key={s.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-out ${i === idx ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            aria-hidden={i !== idx}
          >
            <img
              src={resolve(s.img)}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover ${i === idx ? "animate-ken-burns" : ""}`}
              loading={i === 0 ? "eager" : "lazy"}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/30 to-black/20" />
            <div className="absolute inset-y-0 left-0 w-full md:w-3/5 bg-gradient-to-r from-black/45 via-black/20 to-transparent" />
            {i === idx && (
              <div className="relative container-xl h-full flex flex-col justify-end pb-12 md:pb-20">
                <div className="text-[10px] md:text-xs uppercase tracking-[0.4em] mb-4 flex items-center gap-3 animate-fade-in-up" style={{ color: s.accent }}>
                  <span className="h-px w-12" style={{ background: s.accent }} /> {eyebrow}
                </div>
                <h1 className="editorial-heading text-5xl sm:text-6xl md:text-8xl lg:text-[110px] text-white max-w-5xl leading-[0.9] font-light animate-fade-in-up animation-delay-150">
                  {title}<br /><span className="italic" style={{ color: s.accent }}>{titleAccent}.</span>
                </h1>
                <p className="mt-6 text-base md:text-xl text-white/85 max-w-xl animate-fade-in-up animation-delay-300">{subtitle}</p>
                <div className="mt-8 flex flex-wrap gap-3 animate-fade-in-up animation-delay-500">
                  <Link to={s.to} data-testid={`hero-slide-cta-${s.id}`}>
                    <button className="inline-flex items-center gap-2 px-7 h-12 rounded-sm font-semibold uppercase tracking-wider text-xs hover:bg-white text-black transition-colors" style={{ background: s.accent }}>
                      <s.icon className="h-4 w-4" /> {ctaLabel}
                    </button>
                  </Link>
                  <Link to="/abbonamenti">
                    <button className="inline-flex items-center gap-2 px-7 h-12 bg-white/5 border border-white/40 text-white rounded-sm hover:bg-white hover:text-black transition-colors uppercase tracking-wider text-xs backdrop-blur">
                      {t("home.all_services", "Tutti i servizi")} <ArrowRight className="h-4 w-4" />
                    </button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        );
      })}
      <div className="absolute bottom-6 right-6 md:right-10 flex gap-2 z-20" data-testid="hero-carousel-dots">
        {SLIDES.map((s, i) => (
          <button
            key={s.id}
            onClick={() => setIdx(i)}
            data-testid={`hero-dot-${s.id}`}
            aria-label={`Vai a ${s.id}`}
            className={`h-1 transition-all duration-500 rounded-full ${i === idx ? "w-10 bg-[#E2A03F]" : "w-5 bg-white/30 hover:bg-white/60"}`}
          />
        ))}
      </div>
    </section>
  );
}
